import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Wine, Users, Star, Calendar } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Default site images
const DEFAULT_SITE_IMAGES = {
  rentbar_uv: { url: 'https://customer-assets.emergentagent.com/job_kovacic-shop/artifacts/ed4yuigl_g01.webp', position: 50 },
  rentbar_collection: { url: 'https://customer-assets.emergentagent.com/job_kovacic-shop/artifacts/vhl37o8o_5a-min.webp', position: 50 }
};

export default function RentGinBar() {
  const { language } = useLanguage();
  const [siteImages, setSiteImages] = useState(DEFAULT_SITE_IMAGES);

  useEffect(() => {
    fetch('/api/site-images.php')
      .then(res => res.json())
      .then(data => {
        if (data && !data.error) {
          setSiteImages({ ...DEFAULT_SITE_IMAGES, ...data });
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen pt-32 pb-24 bg-[#0A0A0A]" data-testid="rent-gin-bar-page">
      {/* Hero */}
      <section className="px-6 lg:px-12 mb-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="decorative-line mb-8" />
              <p className="text-gold text-xs tracking-[0.4em] uppercase mb-4">
                {language === 'sr' ? 'Posebna ponuda' : 'Special offer'}
              </p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6">
                {language === 'sr' ? 'Rentiraj Džin Bar' : 'Rent a Gin Bar'}
              </h1>
              <p className="text-white/50 text-lg leading-relaxed mb-8">
                {language === 'sr' 
                  ? 'Učinite vaš događaj nezaboravnim uz naš mobilni džin bar! Donosimo premium džin iskustvo direktno do vas, bilo gde u Srbiji.'
                  : 'Make your event unforgettable with our mobile gin bar! We bring the premium gin experience directly to you, anywhere in Serbia.'}
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-black px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium transition-all duration-300"
              >
                {language === 'sr' ? 'Zatražite ponudu' : 'Request a quote'}
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="relative">
              <div className="overflow-hidden">
                <img
                  src={siteImages.rentbar_uv?.url || DEFAULT_SITE_IMAGES.rentbar_uv.url}
                  alt="UV Gin Cocktails"
                  className="w-full h-auto object-cover max-h-[500px]"
                  style={{ objectPosition: `center ${siteImages.rentbar_uv?.position || 50}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 lg:px-12 py-24 bg-surface">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="decorative-line mx-auto mb-8" />
            <h2 className="font-heading text-3xl md:text-4xl text-white">
              {language === 'sr' ? 'Šta dobijate?' : 'What do you get?'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-6 border border-gold/30 flex items-center justify-center">
                <Wine size={28} className="text-gold" />
              </div>
              <h3 className="font-heading text-xl text-white mb-3">
                {language === 'sr' ? 'Premium Džinovi' : 'Premium Gins'}
              </h3>
              <p className="text-white/50 text-sm">
                {language === 'sr' 
                  ? 'Sva tri ОРИЏИН džina - Classic, Citrus i UV za spektakularne koktele.'
                  : 'All three ORIGIN gins - Classic, Citrus and UV for spectacular cocktails.'}
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-6 border border-gold/30 flex items-center justify-center">
                <Users size={28} className="text-gold" />
              </div>
              <h3 className="font-heading text-xl text-white mb-3">
                {language === 'sr' ? 'Profesionalni Barmeni' : 'Professional Bartenders'}
              </h3>
              <p className="text-white/50 text-sm">
                {language === 'sr' 
                  ? 'Iskusni barmeni koji će pripremiti savršene koktele za vaše goste.'
                  : 'Experienced bartenders who will prepare perfect cocktails for your guests.'}
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-6 border border-gold/30 flex items-center justify-center">
                <Star size={28} className="text-gold" />
              </div>
              <h3 className="font-heading text-xl text-white mb-3">
                {language === 'sr' ? 'Elegantna Oprema' : 'Elegant Equipment'}
              </h3>
              <p className="text-white/50 text-sm">
                {language === 'sr' 
                  ? 'Mobilni bar sa svom potrebnom opremom i priborom visokog kvaliteta.'
                  : 'Mobile bar with all necessary high-quality equipment and accessories.'}
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-6 border border-gold/30 flex items-center justify-center">
                <Calendar size={28} className="text-gold" />
              </div>
              <h3 className="font-heading text-xl text-white mb-3">
                {language === 'sr' ? 'Fleksibilni Termini' : 'Flexible Scheduling'}
              </h3>
              <p className="text-white/50 text-sm">
                {language === 'sr' 
                  ? 'Prilagođavamo se vašim potrebama i terminima za savršen događaj.'
                  : 'We adapt to your needs and schedules for a perfect event.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Events */}
      <section className="px-6 lg:px-12 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="overflow-hidden">
                <img
                  src={siteImages.rentbar_collection?.url || DEFAULT_SITE_IMAGES.rentbar_collection.url}
                  alt="Gin Collection"
                  className="w-full h-auto object-cover max-h-[500px]"
                  style={{ objectPosition: `center ${siteImages.rentbar_collection?.position || 50}%` }}
                />
              </div>
            </div>
            <div>
              <div className="decorative-line mb-8" />
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">
                {language === 'sr' ? 'Idealno za' : 'Perfect for'}
              </h2>
              <ul className="space-y-4 mb-10">
                {[
                  language === 'sr' ? 'Venčanja i proslave' : 'Weddings and celebrations',
                  language === 'sr' ? 'Korporativni događaji' : 'Corporate events',
                  language === 'sr' ? 'Rođendanske zabave' : 'Birthday parties',
                  language === 'sr' ? 'Privatne proslave' : 'Private celebrations',
                  language === 'sr' ? 'Promocije i otvaranja' : 'Promotions and openings',
                  language === 'sr' ? 'Festival i koncerti' : 'Festivals and concerts'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-4 text-white/70">
                    <span className="w-2 h-2 bg-gold" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-white/50 leading-relaxed mb-8">
                {language === 'sr' 
                  ? 'Naš UV džin koji menja boju garantovano će oduševiti vaše goste i učiniti svaki koktel nezaboravnim. Kreirajte magičnu atmosferu uz ljubičaste nijanse koje nastaju pred očima vaših gostiju.'
                  : 'Our color-changing UV gin is guaranteed to delight your guests and make every cocktail unforgettable. Create a magical atmosphere with purple hues that appear before your guests\' eyes.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 lg:px-12 py-24 bg-surface">
        <div className="max-w-3xl mx-auto text-center">
          <div className="decorative-line mx-auto mb-8" />
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
            {language === 'sr' 
              ? 'Zainteresovani ste?' 
              : 'Interested?'}
          </h2>
          <p className="text-white/50 mb-10">
            {language === 'sr' 
              ? 'Kontaktirajte nas za besplatnu ponudu i rezervišite termin za vaš događaj. Rado ćemo odgovoriti na sva vaša pitanja.'
              : 'Contact us for a free quote and book a date for your event. We will be happy to answer all your questions.'}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-black px-10 py-4 text-sm tracking-[0.15em] uppercase font-medium transition-all duration-300"
          >
            {language === 'sr' ? 'Kontaktirajte nas' : 'Contact us'}
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
